import moment from 'moment'
import { LOCALE_DATE_STRING_OPTIONS } from '../../constants'
import type { Tick } from 'chart.js'

const DATE_STRING_OPTIONS = {
  ...LOCALE_DATE_STRING_OPTIONS,
  second: 'numeric'
} as Intl.DateTimeFormatOptions

/**
 * Helps to return duration in Hours if we send start-time and end-time
 *
 * @param minDuration
 * @param maxDuration
 * @returns
 */
export const durationInHours = (minDuration: string, maxDuration: string) => {
  const duration = moment.duration(moment(maxDuration).diff(moment(minDuration)))

  return Math.ceil(duration.asHours())
}

/**
 * This function helps to return proper horizontal bar-width.
 * That is based on:
 *  A. total number of resultSets presetnts on specified time-window and
 *  B. the duration (in hours)of query window
 * Based on above inputs the function will evaluate `barWidth` and stepSize (ie. sequential xAxis label flow in HH:mm format)
 *
 * @param durationInHour
 * @param labelCount
 * @returns { barWidth: number[1-15], stepSize: number[x-15], ticksPadding: number[1-30] }
 */
export const getCustomChartConfigs = (durationInHour: number, labelCount: number) => {
  const DEFAULT_STEP_SIZE = 15 // ie. 15 minutes apart
  const DEFAULT_TICKS_PADDING = 1
  const stepSize = (durationInHour * 60) / labelCount

  // a fictitious variable for grouping output of similar type
  const bucket = durationInHour > 0
    ? Math.floor(labelCount / durationInHour)
    : 0

  // stepSize (ie. x-Axis labels) calucation with restriction
  const getComfortStepSize = (stepSize: number) => Math.min(Math.ceil(stepSize / 5) * 5, DEFAULT_STEP_SIZE)

  const configs = {
    smallerWindow: {
      barWidth: 15,
      stepSize: getComfortStepSize(stepSize),
      ticksPadding: DEFAULT_TICKS_PADDING
    },
    forUpto5Hrs: {
      barWidth: 10,
      stepSize: getComfortStepSize(stepSize),
      ticksPadding: DEFAULT_TICKS_PADDING
    },
    forStandard8Hrs: {
      barWidth: 7,
      stepSize: DEFAULT_STEP_SIZE,
      ticksPadding: 5
    },
    forUpTo10Hrs: {
      barWidth: 6,
      stepSize: DEFAULT_STEP_SIZE,
      ticksPadding: 5
    },
    forUpTo15Hrs: {
      barWidth: 5,
      stepSize: DEFAULT_STEP_SIZE,
      ticksPadding: 15
    },
    forUpTo20Hrs: {
      barWidth: 4,
      stepSize: DEFAULT_STEP_SIZE,
      ticksPadding: 20
    },
    forUpTo24Hrs: {
      barWidth: 3,
      stepSize: DEFAULT_STEP_SIZE,
      ticksPadding: 20
    },
    forUpTo3Days: {
      barWidth: 2,
      stepSize: DEFAULT_STEP_SIZE,
      ticksPadding: 20
    },
    forUpTo7Days: {
      barWidth: 1,
      stepSize: DEFAULT_STEP_SIZE,
      ticksPadding: 25
    },
    beyound3Days: {
      barWidth: 1,
      stepSize: DEFAULT_STEP_SIZE,
      ticksPadding: 25
    }
  }

  // Helps to get Chart's config for specefic duration window (in Hours)
  const getConfig = (duration: number) => {
    const configMap: {
      max: number
      config: {
        barWidth: number
        stepSize: number
        ticksPadding: number
      }
    }[] = [
      {
        max: 1,
        config: configs.smallerWindow
      },
      {
        max: 5,
        config: configs.forUpto5Hrs
      },
      {
        max: 9,
        config: configs.forStandard8Hrs
      },
      {
        max: 12,
        config: configs.forUpTo10Hrs
      },
      {
        max: 15,
        config: configs.forUpTo15Hrs
      },
      {
        max: 20,
        config: configs.forUpTo20Hrs
      },
      {
        max: 24,
        config: configs.forUpTo24Hrs
      },
      {
        max: 72,
        config: configs.forUpTo3Days
      },
      {
        max: 160,
        config: configs.forUpTo7Days
      },
      {
        max: Infinity,
        config: configs.beyound3Days
      }
    ]

    return configMap.find(({ max }) => duration <= max)!.config
  }

  /**
   * bucket:12 for 5 min freq
   * bucket:6 for 10 min freq
   * bucket:4 for 15 min freq
   * bucket:3 for 20 min freq
   * bucket:2 for 30 min freq
   * bucket:1 for 60 min freq
   */
  if ([1, 2, 3, 4, 6, 12].includes(bucket)) {
    return getConfig(durationInHour)
  }

  // bucket:10 for varying freq [Default]
  if ([10].includes(bucket)) {
    return getConfig(durationInHour)
  }

  return getConfig(durationInHour)
}

/**
 * Helps to round the parameterised timeValue
 *
 * @param timeValue
 * @param roundToBound
 * @param roundInMinutes
 * @returns
 */
export const roundToMinutesValue = (timeValue: string, roundToBound: 'upper'|'lower' = 'lower', roundInMinutes: number = 5) => {
  const start = moment(timeValue)

  const remainder = roundToBound === 'lower'
    ? Math.floor(start.minute() / roundInMinutes) * roundInMinutes
    : Math.ceil(start.minute() / roundInMinutes) * roundInMinutes

  return start.set('minutes', remainder).format('yyyy-MM-DD HH:mm:ss')
}

export const formatDate = (dateStr: string | number | object, format: string) => moment(dateStr).format(format)

export const isSameDay = (minDate: string, maxDate: string) => moment(minDate).isSame(moment(maxDate), 'day')

/**
 * Helps to return localise datetime format.
 * This is special in use in tootip ATM
 *
 * @param toDate
 * @returns
 */
export const getFormattedLocaleDate = (toDate: string) => new Date(toDate).toLocaleDateString(navigator.language, DATE_STRING_OPTIONS)

/**
 * Helps to convert total seconds value into corresponding Hour Minute and Seconds
 * Specially in use on Chart's Tooltip
 *
 * @param totalSeconds
 * @returns
 */
export const secondsToHMSconversion = (totalSeconds: number) => {
  const hour = Math.floor(totalSeconds / 3600)
  const minute = Math.floor((totalSeconds % 3600) / 60)
  const second = (totalSeconds % 60).toFixed(3)

  return `Total: ${hour
    ? `${hour}h `
    : ''}${minute
    ? `${minute}m `
    : ''}${second}s`.trim()
}

/**
 * Helps to identify if the day change changed or not on the Chart's label
 *
 * @param context
 * @returns
 */
export const hasDayChanged = (context: string | undefined): boolean => {
  const months = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec']

  if (context && months.includes(context.substr(-3))) {
    return true
  }

  return false
}

/**
 * Helps to return if any tick is a day cutoff day so that we can mark it bold
 *
 * @param ticks
 * @returns
 */
export const tickLabelManager = (ticks: Tick[]) =>
  ticks.reduce((acc: Tick[], tick: Tick, index): Tick[] => {
    const previousTick: Tick = acc.at(index - 1) || ticks.at(0)!

    if (!moment(previousTick.value).isSame(moment(tick.value).startOf('day'), 'day')) {
      // eslint-disable-next-line fp/no-mutating-methods
      acc.push({
        value: tick.value,
        label: moment(tick.value).format('DD MMM')
      })
    } else {
      // eslint-disable-next-line fp/no-mutating-methods
      acc.push({
        value: tick.value,
        label: tick.label
      })
    }

    return acc
  }, [])
